import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type CreditDecisionOutcome = 'APPROVED' | 'DECLINED' | 'INFO_REQUIRED';

export interface CreditDecision {
  id: string;
  loanId: string;
  decidedById: string;
  decision: CreditDecisionOutcome;
  reason?: string;
  conditions?: string;
  infoRequired?: string;
  createdAt: string;
  decidedBy: { id: string; firstName: string; lastName: string };
}

export interface LoanSummaryData {
  loan: {
    id: string;
    loanNumber: string;
    status: string;
    loanType: string;
    principal: number;
    interestRate: number;
    defaultRate: number | null;
    termMonths: number;
    paymentFrequency: string;
    applicationDate: string;
    establishmentFee: number;
    brokerageFee: number;
    legalCosts: number;
    otherCosts: number;
    otherCostsDescription?: string;
  };
  borrower: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    taxId?: string;
    address?: Record<string, string>;
  };
  assignments: any[];
  securities: {
    description: string;
    type: string;
    priority: number;
    priorEncumbrances: number;
    existingDebt: number;
    existingDebtHolder?: string;
    borrowerValuation: number | null;
    internalEstimate: number | null;
    valuerEstimate: number | null;
    valuationDate?: string;
    valuerName?: string;
    status: string;
  }[];
  securitySummary: {
    totalSecurityValue: number;
    totalExistingDebt: number;
    netEquity: number;
    ltv: number | null;
  };
  financials: {
    outstandingPrincipal: number;
    accruedInterest: number;
    totalPayments: number;
    totalOwed: number;
  };
  schedule: {
    periodNumber: number;
    dueDate: string;
    principalDue: number;
    interestDue: number;
    totalDue: number;
    status: string;
  }[];
  creditDecisions: CreditDecision[];
  statusHistory: any[];
}

export function useLoanSummary(loanId: string) {
  return useQuery<LoanSummaryData>({
    queryKey: ['loans', loanId, 'summary'],
    queryFn: () => api.get(`/loans/${loanId}/summary`).then(r => r.data),
    enabled: !!loanId,
  });
}

export function useCreditDecisions(loanId: string) {
  return useQuery<CreditDecision[]>({
    queryKey: ['loans', loanId, 'credit-decisions'],
    queryFn: () => api.get(`/loans/${loanId}/credit-decisions`).then(r => r.data),
    enabled: !!loanId,
  });
}

export function useRecordCreditDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      ...body
    }: {
      loanId: string;
      decision: CreditDecisionOutcome;
      reason?: string;
      conditions?: string;
      infoRequired?: string;
    }) => api.post(`/loans/${loanId}/credit-decisions`, body).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId] });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'credit-decisions'] });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'summary'] });
      qc.invalidateQueries({ queryKey: ['loans', 'list'] });
      qc.invalidateQueries({ queryKey: ['loans', 'board'] });
    },
  });
}
